import React, { useState, useEffect } from 'react';
import { FaPlus, FaTrash, FaEdit, FaUsers, FaTimes, FaMinus, FaProjectDiagram } from 'react-icons/fa';
import './Projects.css';
import apiClient from '../apiClient'; // Import the new apiClient
import ConfirmationModal from '../components/DeleteConfirmationModal';

// Define PMI Phases (match backend)
const PMI_PHASES = ["Initiating", "Planning", "Executing", "Monitoring & Controlling", "Closing"];
const PROJECT_STATUSES = ["Draft", "Active", "In Progress", "Planned", "On Hold", "Completed", "Cancelled"]; // Define valid statuses for frontend

function Projects() {
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    status: 'Draft',
    project_phase: 'Initiating',
    // Project Charter Fields
    project_objective: '',
    business_case: '',
    scope_description: '',
    deliverables: '',
    success_criteria: '',
    key_metrics: '',
    high_level_risks: '',
    constraints: '',
    assumptions: '',
    estimated_budget: '',
    currency: 'USD',
    budget_breakdown: '',
    project_sponsor: '',
    key_stakeholders: '',
    key_milestones: '',
    critical_path_items: '',
    project_priority: '',
    project_type: '',
    complexity_level: '',
    change_impact_level: '',
    affected_departments: '',
    change_readiness_score: '',
    external_project_id: '',
    external_system_url: '',
    approval_status: 'draft',
    approved_by: '',
    approved_date: '',
    custom_fields: '',
    tags: ''
  });
  
  // Risk management state
  const [projectRisks, setProjectRisks] = useState([]);
  
  // Current project stakeholders for populating key_stakeholders
  const [currentProjectStakeholders, setCurrentProjectStakeholders] = useState([]);
  const [showManageModal, setShowManageModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [manageType, setManageType] = useState('stakeholders');
  const [allStakeholders, setAllStakeholders] = useState([]);
  const [projectStakeholders, setProjectStakeholders] = useState([]);
  const [projectGroups, setProjectGroups] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalError, setModalError] = useState('');
  const [modalLoading, setModalLoading] = useState(false);
  const [projectModalError, setProjectModalError] = useState('');
  const [currentStep, setCurrentStep] = useState(1); // Multi-step wizard state
  const [completedSteps, setCompletedSteps] = useState(new Set([1])); // Track completed steps
  const [activeEditTab, setActiveEditTab] = useState('basic'); // Edit tab state
  
  // Confirmation modal state
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: '',
    cancelText: 'Cancel',
    type: 'danger',
    onConfirm: null,
    isLoading: false
  });

  // Helper functions for confirmation modal
  const openConfirmationModal = ({ title, message, confirmText, type = 'danger', onConfirm }) => {
    setConfirmationModal({
      isOpen: true,
      title,
      message,
      confirmText,
      cancelText: 'Cancel',
      type,
      onConfirm,
      isLoading: false
    });
  };

  const closeConfirmationModal = () => {
    setConfirmationModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
  };

  const setConfirmationLoading = (isLoading) => {
    setConfirmationModal(prev => ({ ...prev, isLoading }));
  };

  // Fetch projects
  const fetchProjects = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Use apiClient and remove /api prefix from URL
      const projectsResponse = await apiClient.get('/projects/'); 
      // Axios directly gives data or throws an error for non-2xx responses
      setProjects(projectsResponse.data);
    } catch (e) {
      console.error("Failed to load projects:", e);
      const errorMsg = e.response?.data?.error || e.message || "An unknown error occurred";
      setError(`Failed to load projects: ${errorMsg}. Is the backend running and seeded?`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Risk management functions
  const addRisk = () => {
    const newRisk = {
      id: Date.now(), // Simple ID for frontend
      description: '',
      impact: 'Medium',
      probability: 'Medium',
      mitigation: '',
      owner: '',
      status: 'Open'
    };
    setProjectRisks([...projectRisks, newRisk]);
  };

  const updateRisk = (riskId, field, value) => {
    setProjectRisks(projectRisks.map(risk => 
      risk.id === riskId ? { ...risk, [field]: value } : risk
    ));
  };

  const deleteRisk = (riskId) => {
    const risk = projectRisks.find(r => r.id === riskId);
    openConfirmationModal({
      title: 'Delete Risk',
      message: `Are you sure you want to delete the risk "${risk?.description || 'this risk'}"?`,
      confirmText: 'Delete Risk',
      type: 'danger',
      onConfirm: () => {
        setProjectRisks(projectRisks.filter(risk => risk.id !== riskId));
        closeConfirmationModal();
      }
    });
  };

  // Fetch current project stakeholders for the charter form
  const fetchCurrentProjectStakeholders = async (projectId) => {
    try {
      const res = await apiClient.get(`/projects/${projectId}/stakeholders`);
      const stakeholders = res.data.stakeholders || [];
      setCurrentProjectStakeholders(stakeholders);
      
      // Update formData with current stakeholders
      const stakeholdersList = stakeholders.map(s => 
        `${s.display_name || s.full_name || `${s.first_name} ${s.last_name}`} (${s.job_title || s.organization || 'No title'})`
      );
      setFormData(prev => ({
        ...prev,
        key_stakeholders: JSON.stringify(stakeholdersList, null, 2)
      }));
    } catch (e) {
      console.error("Failed to fetch current project stakeholders:", e);
      setCurrentProjectStakeholders([]);
    }
  };

  // Multi-step wizard functions
  const stepConfig = [
    { id: 1, title: 'Project Basics', icon: '📝', required: ['name', 'status', 'project_phase'] },
    { id: 2, title: 'Charter & Scope', icon: '📋', required: [] },
    { id: 3, title: 'Success & Metrics', icon: '🎯', required: [] },
    { id: 4, title: 'Budget & Resources', icon: '💰', required: [] },
    { id: 5, title: 'Timeline & Stakeholders', icon: '👥', required: [] },
    { id: 6, title: 'Review & Create', icon: '✅', required: [] }
  ];

  const validateStep = (stepId) => {
    const step = stepConfig.find(s => s.id === stepId);
    if (!step?.required?.length) return true;
    
    return step.required.every(field => formData[field] && formData[field].trim() !== '');
  };

  const goToStep = (stepId) => {
    // Validate current step before moving forward
    if (stepId > currentStep && !validateStep(currentStep)) {
      setProjectModalError(`Please complete all required fields in ${stepConfig.find(s => s.id === currentStep)?.title}`);
      return;
    }
    
    // Mark current step as completed when moving forward
    if (stepId > currentStep) {
      setCompletedSteps(prev => new Set([...prev, currentStep]));
    }
    
    setCurrentStep(stepId);
    setProjectModalError(''); // Clear errors when navigating
  };

  const nextStep = () => {
    if (currentStep < stepConfig.length) {
      goToStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      goToStep(currentStep - 1);
    }
  };

  const canProceedToStep = (stepId) => {
    // Can always go to completed steps or current step
    if (stepId <= currentStep || completedSteps.has(stepId)) return true;
    
    // Can only go to next step if current step is valid
    return stepId === currentStep + 1 && validateStep(currentStep);
  };

  // Wizard step rendering
  const renderWizardStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="wizard-step-content">
            <div className="form-group">
              <label htmlFor="name">Project Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="3"
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="status">Status *</label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  required
                >
                  <option value="Draft">Draft</option>
                  <option value="Active">Active</option>
                  <option value="On Hold">On Hold</option>
                  <option value="Completed">Completed</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="project_phase">Project Phase *</label>
                <select
                  id="project_phase"
                  name="project_phase"
                  value={formData.project_phase}
                  onChange={handleInputChange}
                  required
                >
                  <option value="Initiating">Initiating</option>
                  <option value="Planning">Planning</option>
                  <option value="Executing">Executing</option>
                  <option value="Monitoring">Monitoring</option>
                  <option value="Closing">Closing</option>
                </select>
              </div>
            </div>
          </div>
        );
      
      case 2:
        return (
          <div className="wizard-step-content">
            <div className="form-group">
              <label htmlFor="project_objective">Project Objective</label>
              <textarea
                id="project_objective"
                name="project_objective"
                value={formData.project_objective}
                onChange={handleInputChange}
                rows="3"
                placeholder="Clear statement of what the project aims to achieve"
              />
            </div>
            <div className="form-group">
              <label htmlFor="business_case">Business Case</label>
              <textarea
                id="business_case"
                name="business_case"
                value={formData.business_case}
                onChange={handleInputChange}
                rows="3"
                placeholder="Why this project is necessary and valuable"
              />
            </div>
            <div className="form-group">
              <label htmlFor="scope_description">Scope Description</label>
              <textarea
                id="scope_description"
                name="scope_description"
                value={formData.scope_description}
                onChange={handleInputChange}
                rows="3"
                placeholder="What is included and excluded from this project"
              />
            </div>
            <div className="form-group">
              <label htmlFor="deliverables">Key Deliverables</label>
              <textarea
                id="deliverables"
                name="deliverables"
                value={formData.deliverables}
                onChange={handleInputChange}
                rows="3"
                placeholder="Major outputs, products, or results"
              />
            </div>
          </div>
        );
      
      case 3:
        return (
          <div className="wizard-step-content">
            <div className="form-group">
              <label htmlFor="success_criteria">Success Criteria</label>
              <textarea
                id="success_criteria"
                name="success_criteria"
                value={formData.success_criteria}
                onChange={handleInputChange}
                rows="3"
                placeholder="How will you know the project is successful?"
              />
            </div>
            <div className="form-group">
              <label htmlFor="key_metrics">Key Metrics</label>
              <textarea
                id="key_metrics"  
                name="key_metrics"
                value={formData.key_metrics}
                onChange={handleInputChange}
                rows="3"
                placeholder="Specific, measurable indicators of success"
              />
            </div>
            <div className="form-group">
              <label htmlFor="project_priority">Project Priority</label>
              <select
                id="project_priority"
                name="project_priority"
                value={formData.project_priority}
                onChange={handleInputChange}
              >
                <option value="">Select Priority</option>
                <option value="Critical">Critical</option>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
            </div>
          </div>
        );
      
      case 4:
        return (
          <div className="wizard-step-content">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="estimated_budget">Estimated Budget</label>
                <input
                  type="number"
                  id="estimated_budget"
                  name="estimated_budget"
                  value={formData.estimated_budget}
                  onChange={handleInputChange}
                  placeholder="0"
                />
              </div>
              <div className="form-group">
                <label htmlFor="currency">Currency</label>
                <select
                  id="currency"
                  name="currency"
                  value={formData.currency}
                  onChange={handleInputChange}
                >
                  <option value="USD">USD</option>
                  <option value="CAD">CAD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="budget_breakdown">Budget Breakdown</label>
              <textarea
                id="budget_breakdown"
                name="budget_breakdown"
                value={formData.budget_breakdown}
                onChange={handleInputChange}
                rows="3"
                placeholder="Personnel: 60%, Technology: 30%, Other: 10%"
              />
            </div>
            <div className="form-group">
              <label htmlFor="project_sponsor">Project Sponsor</label>
              <input
                type="text"
                id="project_sponsor"
                name="project_sponsor"
                value={formData.project_sponsor}
                onChange={handleInputChange}
                placeholder="Name and title of the project sponsor"
              />
            </div>
          </div>
        );
      
      case 5:
        return (
          <div className="wizard-step-content">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="start_date">Start Date</label>
                <input
                  type="date"
                  id="start_date"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label htmlFor="end_date">End Date</label>
                <input
                  type="date"
                  id="end_date"
                  name="end_date"
                  value={formData.end_date}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="key_milestones">Key Milestones</label>
              <textarea
                id="key_milestones"
                name="key_milestones"
                value={formData.key_milestones}
                onChange={handleInputChange}
                rows="3"
                placeholder="Major project milestones and target dates"
              />
            </div>
            <div className="form-group">
              <label htmlFor="key_stakeholders">Key Stakeholders</label>
              <textarea
                id="key_stakeholders"
                name="key_stakeholders"
                value={formData.key_stakeholders}
                onChange={handleInputChange}
                rows="3"
                placeholder="Important stakeholders and their roles"
              />
            </div>
          </div>
        );
      
      case 6:
        return (
          <div className="wizard-step-content">
            <div className="review-section">
              <h4>📝 Project Summary</h4>
              <div className="review-item">
                <strong>Name:</strong> {formData.name || 'Not specified'}
              </div>
              <div className="review-item">
                <strong>Status:</strong> {formData.status || 'Not specified'}
              </div>
              <div className="review-item">
                <strong>Phase:</strong> {formData.project_phase || 'Not specified'}
              </div>
              <div className="review-item">
                <strong>Priority:</strong> {formData.project_priority || 'Not specified'}
              </div>
              <div className="review-item">
                <strong>Budget:</strong> {formData.estimated_budget ? `${formData.currency} ${formData.estimated_budget}` : 'Not specified'}
              </div>
              <div className="review-item">
                <strong>Timeline:</strong> {formData.start_date && formData.end_date ? `${formData.start_date} to ${formData.end_date}` : 'Not specified'}
              </div>
            </div>
            <div className="review-note">
              <p>✅ Review your project details above. Click "Create Project" to proceed.</p>
            </div>
          </div>
        );
      
      default:
        return <div>Invalid step</div>;
    }
  };

  // Edit tabs rendering
  const renderEditTabs = () => {
    const tabs = [
      { id: 'basic', title: 'Basic Info', icon: '📝' },
      { id: 'charter', title: 'Charter', icon: '📋' },
      { id: 'metrics', title: 'Metrics', icon: '🎯' },
      { id: 'budget', title: 'Budget', icon: '💰' },
      { id: 'timeline', title: 'Timeline', icon: '⏰' },
      { id: 'risks', title: 'Risks', icon: '⚠️' },
      { id: 'advanced', title: 'Advanced', icon: '⚙️' }
    ];

    return (
      <>
        {/* Tab Navigation */}
        <div className="edit-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              type="button"
              className={`edit-tab ${activeEditTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveEditTab(tab.id)}
            >
              <span className="tab-icon">{tab.icon}</span>
              {tab.title}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="edit-tab-content">
          {renderEditTabContent()}
        </div>

        {/* Submit Button */}
        <div className="edit-actions">
          <button type="button" onClick={handleSubmit} className="btn btn-primary">
            Update Project
          </button>
        </div>
      </>
    );
  };

  const renderEditTabContent = () => {
    switch (activeEditTab) {
      case 'basic':
        return (
          <div className="tab-content-section">
            <div className="form-group">
              <label htmlFor="name">Project Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="3"
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="status">Status *</label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  required
                >
                  <option value="Draft">Draft</option>
                  <option value="Active">Active</option>
                  <option value="On Hold">On Hold</option>
                  <option value="Completed">Completed</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="project_phase">Project Phase</label>
                <select
                  id="project_phase"
                  name="project_phase"
                  value={formData.project_phase}
                  onChange={handleInputChange}
                >
                  <option value="Initiating">Initiating</option>
                  <option value="Planning">Planning</option>
                  <option value="Executing">Executing</option>
                  <option value="Monitoring">Monitoring</option>
                  <option value="Closing">Closing</option>
                </select>
              </div>
            </div>
          </div>
        );
        
      case 'charter':
        return (
          <div className="tab-content-section">
            <div className="form-group">
              <label htmlFor="project_objective">Project Objective</label>
              <textarea
                id="project_objective"
                name="project_objective"
                value={formData.project_objective}
                onChange={handleInputChange}
                rows="3"
              />
            </div>
            <div className="form-group">
              <label htmlFor="business_case">Business Case</label>
              <textarea
                id="business_case"
                name="business_case"
                value={formData.business_case}
                onChange={handleInputChange}
                rows="3"
              />
            </div>
            <div className="form-group">
              <label htmlFor="scope_description">Scope Description</label>
              <textarea
                id="scope_description"
                name="scope_description"
                value={formData.scope_description}
                onChange={handleInputChange}
                rows="3"
              />
            </div>
          </div>
        );
        
      case 'metrics':
        return (
          <div className="tab-content-section">
            <div className="form-group">
              <label htmlFor="success_criteria">Success Criteria</label>
              <textarea
                id="success_criteria"
                name="success_criteria"
                value={formData.success_criteria}
                onChange={handleInputChange}
                rows="3"
              />
            </div>
            <div className="form-group">
              <label htmlFor="key_metrics">Key Metrics</label>
              <textarea
                id="key_metrics"
                name="key_metrics"
                value={formData.key_metrics}
                onChange={handleInputChange}
                rows="3"
              />
            </div>
          </div>
        );
        
      case 'budget':
        return (
          <div className="tab-content-section">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="estimated_budget">Estimated Budget</label>
                <input
                  type="number"
                  id="estimated_budget"
                  name="estimated_budget"
                  value={formData.estimated_budget}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label htmlFor="currency">Currency</label>
                <select
                  id="currency"
                  name="currency"
                  value={formData.currency}
                  onChange={handleInputChange}
                >
                  <option value="USD">USD</option>
                  <option value="CAD">CAD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="budget_breakdown">Budget Breakdown (category: amount)</label>
              <textarea id="budget_breakdown" name="budget_breakdown" value={formData.budget_breakdown} onChange={handleInputChange} rows="3" />
            </div>
          </div>
        );
        
      case 'timeline':
        return (
          <div className="tab-content-section">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="start_date">Start Date</label>
                <input
                  type="date"
                  id="start_date"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label htmlFor="end_date">End Date</label>
                <input
                  type="date"
                  id="end_date"
                  name="end_date"
                  value={formData.end_date}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="key_milestones">Key Milestones (milestone: date)</label>
              <textarea id="key_milestones" name="key_milestones" value={formData.key_milestones} onChange={handleInputChange} rows="3" />
            </div>
          </div>
        );
        
      case 'risks':
        return (
          <div className="tab-content-section">
            <div className="form-group">
              <label htmlFor="high_level_risks">High Level Risks (one per line)</label>
              <textarea
                id="high_level_risks"
                name="high_level_risks"
                value={formData.high_level_risks}
                onChange={handleInputChange}
                rows="3"
                placeholder="Describe high-level risks and potential impacts"
              />
            </div>
            <div className="form-group">
              <label htmlFor="constraints">Constraints (one per line)</label>
              <textarea
                id="constraints"
                name="constraints"
                value={formData.constraints}
                onChange={handleInputChange}
                rows="3"
                placeholder="Budget must not exceed $2M, Project must be completed by Q4 2024"
              />
            </div>
            <div className="form-group">
              <label htmlFor="assumptions">Assumptions (one per line)</label>
              <textarea
                id="assumptions"
                name="assumptions"
                value={formData.assumptions}
                onChange={handleInputChange}
                rows="3"
                placeholder="Key team members will be available, Budget approval will be obtained"
              />
            </div>
            <div className="form-group">
              <label htmlFor="change_readiness_score">Change Readiness Score (1-10)</label>
              <input 
                type="number" 
                min="1" 
                max="10" 
                step="0.1"
                id="change_readiness_score" 
                name="change_readiness_score" 
                value={formData.change_readiness_score} 
                onChange={handleInputChange} 
                placeholder="Rate 1-10"
              />
            </div>
          </div>
        );
        
      case 'advanced':
        return (
          <div className="tab-content-section">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="project_priority">Priority</label>
                <select
                  id="project_priority"
                  name="project_priority"
                  value={formData.project_priority}
                  onChange={handleInputChange}
                >
                  <option value="">Select Priority</option>
                  <option value="Critical">Critical</option>
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="complexity_level">Complexity Level</label>
                <select
                  id="complexity_level"
                  name="complexity_level"
                  value={formData.complexity_level}
                  onChange={handleInputChange}
                >
                  <option value="">Select Complexity</option>
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Very High">Very High</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="external_project_id">External Project ID</label>
              <input
                type="text"
                id="external_project_id"
                name="external_project_id"
                value={formData.external_project_id}
                onChange={handleInputChange}
              />
            </div>
          </div>
        );
        
      default:
        return <div>Select a tab to edit project details</div>;
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingProject(null);
    setProjectModalError('');
    setProjectRisks([]);
    setCurrentProjectStakeholders([]);
    setCurrentStep(1); // Reset to first step when closing
    setCompletedSteps(new Set()); // Reset completed steps
    setActiveEditTab('basic'); // Reset to first tab for edit mode
    setFormData({
      name: '',
      description: '',
      start_date: '',
      end_date: '',
      status: 'Draft',
      project_phase: 'Initiating',
      // Project Charter Fields
      project_objective: '',
      business_case: '',
      scope_description: '',
      deliverables: '',
      success_criteria: '',
      key_metrics: '',
      high_level_risks: '',
      constraints: '',
      assumptions: '',
      estimated_budget: '',
      currency: 'USD',
      budget_breakdown: '',
      project_sponsor: '',
      key_stakeholders: '',
      key_milestones: '',
      critical_path_items: '',
      project_priority: '',
      project_type: '',
      complexity_level: '',
      change_impact_level: '',
      affected_departments: '',
      change_readiness_score: '',
      external_project_id: '',
      external_system_url: '',
      approval_status: 'draft',
      approved_by: '',
      approved_date: '',
      custom_fields: '',
      tags: ''
    });
  };

  const openCreateModal = () => {
    try {
      console.log('🚀 openCreateModal called - starting project creation modal');
      
      // Just do the absolute minimum to open the modal
      setEditingProject(null);
      setProjectModalError('');
      
      // Initialize all required state variables
      setProjectRisks([]);
      setCurrentProjectStakeholders([]);
      
      // Set minimal formData to prevent undefined errors in modal rendering
      setFormData({
        name: '',
        description: '',
        start_date: '',
        end_date: '',
        status: 'Draft',
        project_phase: 'Initiating',
        project_objective: '',
        business_case: '',
        scope_description: '',
        deliverables: '',
        success_criteria: '',
        key_metrics: '',
        high_level_risks: '',
        constraints: '',
        assumptions: '',
        estimated_budget: '',
        currency: 'USD',
        budget_breakdown: '',
        project_sponsor: '',
        key_stakeholders: '',
        key_milestones: '',
        critical_path_items: '',
        project_priority: '',
        project_type: '',
        complexity_level: '',
        change_impact_level: '',
        affected_departments: '',
        change_readiness_score: '',
        external_project_id: '',
        external_system_url: '',
        approval_status: 'draft',
        approved_by: '',
        approved_date: '',
        custom_fields: '',
        tags: ''
      });
      
      console.log('✅ Form data initialized');
      
      setIsModalOpen(true);
      
      console.log('✅ Minimal modal opened successfully');
    } catch (error) {
      console.error('❌ Error in openCreateModal:', error);
      console.error('Error details:', error.message, error.stack);
      alert('Modal error: ' + error.message); // Use alert so it persists even if page redirects
    }
  };

  const openEditModal = async (project) => {
    
    
    try {
      setEditingProject(project);
      setProjectModalError('');
      
      // Helper function to convert JSON to user-friendly format
      const parseJsonToUserFriendly = (field, fieldName = 'unknown') => {
        if (!field) return '';
        
        try {
          const parsed = typeof field === 'string' ? JSON.parse(field) : field;
          
          if (Array.isArray(parsed)) {
            // Convert array to line-separated text
            return parsed.join('\n');
          } else if (typeof parsed === 'object' && parsed !== null) {
            // Convert object to key: value format, one per line
            return Object.entries(parsed)
              .map(([key, value]) => `${key}: ${value}`)
              .join('\n');
          } else {
            return String(parsed);
          }
        } catch (error) {
          console.warn(`Failed to parse JSON field '${fieldName}':`, field, error);
          // If parsing fails, return as-is
          return String(field || '');
        }
      };

      // Parse risks from high_level_risks field
      let risks = [];
      if (project.high_level_risks) {
        try {
          // Safely parse JSON with validation
          const risksString = String(project.high_level_risks).trim();
          if (risksString && risksString !== "null" && risksString !== "undefined" && risksString.length >= 2) {
            const parsedRisks = JSON.parse(risksString);
            if (Array.isArray(parsedRisks)) {
              // Check if it's structured risks or just strings
              risks = parsedRisks.map((risk, index) => {
                if (typeof risk === 'string') {
                  // Convert simple string to structured risk
                  return {
                    id: Date.now() + index,
                    description: risk,
                    impact: 'Medium',
                    probability: 'Medium',
                    mitigation: '',
                    owner: '',
                    status: 'Open'
                  };
                } else {
                  // Already structured
                  return {
                    id: risk.id || Date.now() + index,
                    ...risk
                  };
                }
              });
            }
          }
        } catch (error) {
          console.warn('Failed to parse risks for project:', project.name, error);
          // If parsing fails, treat as simple text and create one risk
          if (typeof project.high_level_risks === 'string' && project.high_level_risks.trim()) {
            risks = [{
              id: Date.now(),
              description: project.high_level_risks,
              impact: 'Medium',
              probability: 'Medium',
              mitigation: '',
              owner: '',
              status: 'Open'
            }];
          }
        }
      }
      setProjectRisks(risks);
      
      // Safe date parsing
      const safeParseDate = (dateString) => {
        if (!dateString) return '';
        try {
          if (typeof dateString === 'string' && dateString.includes('T')) {
            return dateString.split('T')[0];
          }
          return dateString;
        } catch (error) {
          console.warn('Failed to parse date:', dateString, error);
          return '';
        }
      };
      
      // Set form data first
      setFormData({
        name: project.name || '',
        description: project.description || '',
        start_date: safeParseDate(project.start_date),
        end_date: safeParseDate(project.end_date),
        status: project.status || 'Draft',
        project_phase: project.project_phase || 'Initiating',
        // Project Charter Fields
        project_objective: project.project_objective || '',
        business_case: project.business_case || '',
        scope_description: project.scope_description || '',
        deliverables: parseJsonToUserFriendly(project.deliverables, 'deliverables'),
        success_criteria: parseJsonToUserFriendly(project.success_criteria, 'success_criteria'),
        key_metrics: parseJsonToUserFriendly(project.key_metrics, 'key_metrics'),
        high_level_risks: '', // Will be populated from projectRisks state
        constraints: parseJsonToUserFriendly(project.constraints, 'constraints'),
        assumptions: parseJsonToUserFriendly(project.assumptions, 'assumptions'),
        estimated_budget: project.estimated_budget || '',
        currency: project.currency || 'USD',
        budget_breakdown: parseJsonToUserFriendly(project.budget_breakdown, 'budget_breakdown'),
        project_sponsor: project.project_sponsor || '',
        key_stakeholders: '', // Will be populated from current stakeholders
        key_milestones: parseJsonToUserFriendly(project.key_milestones, 'key_milestones'),
        critical_path_items: parseJsonToUserFriendly(project.critical_path_items, 'critical_path_items'),
        project_priority: project.project_priority || '',
        project_type: project.project_type || '',
        complexity_level: project.complexity_level || '',
        change_impact_level: project.change_impact_level || '',
        affected_departments: parseJsonToUserFriendly(project.affected_departments, 'affected_departments'),
        change_readiness_score: project.change_readiness_score || '',
        external_project_id: project.external_project_id || '',
        external_system_url: project.external_system_url || '',
        approval_status: project.approval_status || 'draft',
        approved_by: project.approved_by || '',
        approved_date: safeParseDate(project.approved_date),
        custom_fields: parseJsonToUserFriendly(project.custom_fields, 'custom_fields'),
        tags: parseJsonToUserFriendly(project.tags, 'tags')
      });
      
      
      
      // Open modal immediately - don't wait for async operations
      setIsModalOpen(true);
      
      
      // Fetch current stakeholders asynchronously and update form when complete
      try {
        await fetchCurrentProjectStakeholders(project.id);
        
      } catch (error) {
        console.warn('Could not fetch stakeholders for project:', project.id, error);
        setCurrentProjectStakeholders([]);
      }
      
    } catch (error) {
      console.error('❌ Critical error in openEditModal for project:', project.name, error);
      setProjectModalError(`Failed to open edit form: ${error.message}`);
      // Try to open modal anyway with minimal data
      setFormData({
        name: project.name || '',
        description: project.description || '',
        start_date: '',
        end_date: '',
        status: project.status || 'Draft',
        project_phase: project.project_phase || 'Initiating',
        project_objective: '',
        business_case: '',
        scope_description: '',
        deliverables: '',
        success_criteria: '',
        key_metrics: '',
        high_level_risks: '',
        constraints: '',
        assumptions: '',
        estimated_budget: '',
        currency: 'USD',
        budget_breakdown: '',
        project_sponsor: '',
        key_stakeholders: '',
        key_milestones: '',
        critical_path_items: '',
        project_priority: '',
        project_type: '',
        complexity_level: '',
        change_impact_level: '',
        affected_departments: '',
        change_readiness_score: '',
        external_project_id: '',
        external_system_url: '',
        approval_status: 'draft',
        approved_by: '',
        approved_date: '',
        custom_fields: '',
        tags: ''
      });
      setIsModalOpen(true);
    }
  };

  const openManageModal = (project) => {
    
    
    try {
      
      setSelectedProject(project);
      
      
      setShowManageModal(true);
      
      
      setManageType('stakeholders');
      
      
      setSearchTerm('');
      
      
      setModalError(''); // Clear any previous errors
      
      
      setModalLoading(true); // Show loading state
      
      
      // Call async functions and handle errors
      Promise.all([
        fetchProjectStakeholders(project.id),
        fetchProjectGroups(project.id),
        fetchAllMembers()
      ]).then(() => {
        
        setModalLoading(false);
      }).catch((error) => {
        console.error('❌ Error fetching modal data:', error);
        setModalError(`Failed to load project data: ${error.message}`);
        setModalLoading(false);
      });
      
      
    } catch (error) {
      console.error('❌ Critical error in openManageModal:', error);
      
      setModalError(`Failed to open member management: ${error.message}`);
      setModalLoading(false);
    }
  };

  const closeManageModal = () => {
    setShowManageModal(false);
    setSelectedProject(null);
  };


  const handleSubmit = async (e) => {
    try {
      console.log('🚀 handleSubmit called - starting form submission');
      e.preventDefault();
      
      // Adjust URL for apiClient (remove /api prefix)
      const url = editingProject ? `/projects/${editingProject.id}` : '/projects/';
      const method = editingProject ? 'put' : 'post'; // Axios methods are lowercase

      console.log('✅ URL and method determined:', { url, method });

      // Helper function to convert user-friendly format back to JSON for submission
      const parseJsonForSubmit = (field) => {
        if (!field || field.trim() === '') return null;
        
        // Split by lines and filter out empty lines
        const lines = field.split('\n').filter(line => line.trim() !== '');
        if (lines.length === 0) return null;
        
        // Check if it looks like key: value pairs
        const hasKeyValuePairs = lines.some(line => line.includes(':') && line.indexOf(':') > 0);
        
        if (hasKeyValuePairs) {
          // Convert to object
          const obj = {};
          lines.forEach(line => {
            const colonIndex = line.indexOf(':');
            if (colonIndex > 0) {
              const key = line.substring(0, colonIndex).trim();
              const value = line.substring(colonIndex + 1).trim();
              obj[key] = value;
            }
          });
          return obj;
        } else {
          // Return as array
          return lines;
        }
      };

      console.log('✅ Helper function defined');

      // Safely handle stakeholders
      const safeStakeholders = Array.isArray(currentProjectStakeholders) ? currentProjectStakeholders : [];
      
      const payload = {
          ...formData,
          start_date: formData.start_date || null,
          end_date: formData.end_date || null,
          approved_date: formData.approved_date || null,
          estimated_budget: formData.estimated_budget ? parseFloat(formData.estimated_budget) : null,
          change_readiness_score: formData.change_readiness_score ? parseInt(formData.change_readiness_score) : null,
          // Convert JSON fields
          deliverables: parseJsonForSubmit(formData.deliverables),
          success_criteria: parseJsonForSubmit(formData.success_criteria),
          key_metrics: parseJsonForSubmit(formData.key_metrics),
          high_level_risks: projectRisks.length > 0 ? projectRisks : null, // Use structured risks
          constraints: parseJsonForSubmit(formData.constraints),
          assumptions: parseJsonForSubmit(formData.assumptions),
          budget_breakdown: parseJsonForSubmit(formData.budget_breakdown),
          key_stakeholders: safeStakeholders.map(s => 
            `${s.display_name || s.full_name || `${s.first_name} ${s.last_name}`} (${s.job_title || s.organization || 'No title'})`
          ), // Use current assigned stakeholders safely
          key_milestones: parseJsonForSubmit(formData.key_milestones),
          critical_path_items: parseJsonForSubmit(formData.critical_path_items),
          affected_departments: parseJsonForSubmit(formData.affected_departments),
          custom_fields: parseJsonForSubmit(formData.custom_fields),
          tags: parseJsonForSubmit(formData.tags)
      };

      console.log('✅ Payload created:', { name: payload.name, status: payload.status, stakeholders: safeStakeholders.length });

      if (!payload.name || !payload.status || !payload.project_phase) {
          setProjectModalError('Project Name, Status, and Phase are required.');
          console.log('❌ Validation failed: missing required fields');
          return;
      }

      console.log('✅ Validation passed, making API call...');

      // Debug user authentication
      const token = localStorage.getItem('access_token');
      const userRole = localStorage.getItem('userRole');
      const ezocmUser = JSON.parse(localStorage.getItem('ezocmUser') || '{}');
      
      console.log('🔑 Authentication Debug:', {
        hasToken: !!token,
        tokenStart: token ? token.substring(0, 20) + '...' : 'No token',
        userRole: userRole,
        userName: ezocmUser.name,
        userEmail: ezocmUser.email
      });

      // Use apiClient with dynamic method
      await apiClient[method](url, payload);
      
      console.log('✅ API call successful');
      
      closeModal();
      fetchProjects();
      
      console.log('✅ Form submission completed successfully');
    } catch (error) {
      console.error('❌ Error in handleSubmit:', error);
      console.error('Error details:', error.message, error.stack);
      const errorMsg = error.response?.data?.error || error.message || "An unknown error occurred";
      setProjectModalError(`Failed to ${editingProject ? 'update' : 'create'} project: ${errorMsg}`);
      alert('Form submission error: ' + error.message); // Use alert so it persists
    }
  };

  const handleDeleteProject = async (projectId) => {
    const project = projects.find(p => p.id === projectId);
    openConfirmationModal({
      title: 'Delete Project',
      message: `Are you sure you want to delete the project "${project?.name || 'this project'}"? This action cannot be undone.`,
      confirmText: 'Delete Project',
      type: 'danger',
      onConfirm: async () => {
        setConfirmationLoading(true);
        try {
          await apiClient.delete(`/projects/${projectId}`);
          fetchProjects();
          closeConfirmationModal();
        } catch (e) {
          console.error("Failed to delete project:", e);
          const errorMsg = e.response?.data?.error || e.message || "An unknown error occurred";
          setError(`Failed to delete project: ${errorMsg}`);
          closeConfirmationModal();
        }
      }
    });
  };

  const fetchProjectStakeholders = async (projectId) => {
    try {
      const res = await apiClient.get(`/projects/${projectId}/stakeholders`);
      const stakeholders = res.data.stakeholders || [];
      
      setProjectStakeholders(stakeholders);
    } catch (e) { 
      console.error("Failed to fetch project stakeholders:", e);
      setProjectStakeholders([]); 
    }
  };
  const fetchProjectGroups = async (projectId) => {
    try {
      const res = await apiClient.get(`/stakeholder_groups?project_id=${projectId}`);
      setProjectGroups(res.data || []);
    } catch (e) { 
      console.error("Failed to fetch project groups:", e);
      setProjectGroups([]); 
    }
  };
  const fetchAllMembers = async () => {
    try {
      // Always try to fetch employees first
      console.log('🔍 Fetching employees from /employees endpoint...');
      const employeesRes = await apiClient.get('/employees?per_page=100'); // Increase limit for employees
      console.log('🔍 Raw employees response:', employeesRes);
      console.log('🔍 Employees response data:', employeesRes.data);
      
      // Handle employees response format
      let employees = [];
      if (employeesRes.data?.employees && Array.isArray(employeesRes.data.employees)) {
        console.log('✅ Employees data found in .employees');
        employees = employeesRes.data.employees;
      } else if (Array.isArray(employeesRes.data)) {
        console.log('✅ Employees data is direct array');
        employees = employeesRes.data;
      } else {
        console.log('🔍 Unexpected employees response format:', employeesRes.data);
        console.log('🔍 Available keys:', Object.keys(employeesRes.data || {}));
        employees = [];
      }
      
      console.log('🔍 Final employees array:', employees);
      console.log('🔍 Employees count:', employees.length);
      
      // Ensure we have an array before mapping
      if (!Array.isArray(employees)) {
        console.error('❌ Employees is still not an array, forcing to empty array');
        employees = [];
      }
      
      // Transform employees to have consistent format
      const transformedEmployees = employees.map(emp => ({
        id: `employee_${emp.id}`,
        type: 'employee',
        employee_id: emp.id,
        display_name: emp.name,
        full_name: emp.name,
        first_name: emp.name?.split(' ')[0] || '',
        last_name: emp.name?.split(' ').slice(1).join(' ') || '',
        email: emp.email,
        job_title: emp.job_position,
        organization: emp.department,
        stakeholder_category: 'internal'
      }));

      let transformedStakeholders = [];
      
      // Fetch stakeholders from the comprehensive stakeholder system
      try {
        console.log('🔍 Fetching stakeholders from /v2/stakeholders endpoint...');
        const stakeholdersRes = await apiClient.get('/v2/stakeholders?per_page=100');
        console.log('🔍 Raw stakeholders response:', stakeholdersRes);
        console.log('🔍 Stakeholders response data:', stakeholdersRes.data);
        console.log('🔍 Stakeholders response status:', stakeholdersRes.status);
        
        // Handle stakeholders response format
        let stakeholdersData = [];
        if (stakeholdersRes.data?.stakeholders && Array.isArray(stakeholdersRes.data.stakeholders)) {
          console.log('✅ Stakeholders data found in .stakeholders');
          stakeholdersData = stakeholdersRes.data.stakeholders;
        } else if (Array.isArray(stakeholdersRes.data)) {
          console.log('✅ Stakeholders data is direct array');
          stakeholdersData = stakeholdersRes.data;
        } else {
          console.log('🔍 Unexpected stakeholders response format:', stakeholdersRes.data);
          console.log('🔍 Available keys:', Object.keys(stakeholdersRes.data || {}));
          stakeholdersData = [];
        }
        
        console.log('🔍 Final stakeholdersData array:', stakeholdersData);
        console.log('🔍 Stakeholders count:', stakeholdersData.length);
        
        // Transform stakeholders to have consistent format
        transformedStakeholders = stakeholdersData.map(stakeholder => ({
          id: `stakeholder_${stakeholder.id}`,
          type: 'stakeholder',
          stakeholder_id: stakeholder.id,
          display_name: stakeholder.display_name || `${stakeholder.first_name} ${stakeholder.last_name}`,
          full_name: `${stakeholder.first_name} ${stakeholder.last_name}`,
          first_name: stakeholder.first_name,
          last_name: stakeholder.last_name,
          email: stakeholder.email,
          job_title: stakeholder.job_title,
          organization: stakeholder.organization,
          stakeholder_category: stakeholder.stakeholder_category
        }));
      } catch (stakeholderError) {
        console.error("❌ Could not fetch stakeholders from comprehensive system:", stakeholderError);
        console.error("Stakeholder error response:", stakeholderError.response?.data);
        console.error("Stakeholder error status:", stakeholderError.response?.status);
        console.error("Stakeholder error message:", stakeholderError.message);
        
        // Try the legacy stakeholder endpoint as fallback
        try {
          console.log('🔄 Trying legacy stakeholder endpoint /stakeholders...');
          const legacyStakeholdersRes = await apiClient.get('/stakeholders?per_page=100');
          console.log('🔍 Legacy stakeholders response:', legacyStakeholdersRes.data);
          
          let legacyStakeholdersData = [];
          if (legacyStakeholdersRes.data?.stakeholders && Array.isArray(legacyStakeholdersRes.data.stakeholders)) {
            legacyStakeholdersData = legacyStakeholdersRes.data.stakeholders;
          } else if (Array.isArray(legacyStakeholdersRes.data)) {
            legacyStakeholdersData = legacyStakeholdersRes.data;
          }
          
          console.log('🔍 Legacy stakeholders count:', legacyStakeholdersData.length);
          
          transformedStakeholders = legacyStakeholdersData.map(stakeholder => ({
            id: `stakeholder_${stakeholder.id}`,
            type: 'stakeholder',
            stakeholder_id: stakeholder.id,
            display_name: stakeholder.name || `${stakeholder.first_name || ''} ${stakeholder.last_name || ''}`.trim(),
            full_name: stakeholder.name || `${stakeholder.first_name || ''} ${stakeholder.last_name || ''}`.trim(),
            first_name: stakeholder.first_name || stakeholder.name?.split(' ')[0] || '',
            last_name: stakeholder.last_name || stakeholder.name?.split(' ').slice(1).join(' ') || '',
            email: stakeholder.email,
            job_title: stakeholder.job_position,
            organization: stakeholder.organization || stakeholder.department,
            stakeholder_category: 'external'
          }));
        } catch (legacyError) {
          console.error("❌ Legacy stakeholder endpoint also failed:", legacyError);
          console.error("Legacy error response:", legacyError.response?.data);
          console.error("Legacy error status:", legacyError.response?.status);
        }
      }
      
      // Combine all members
      const allMembers = [...transformedEmployees, ...transformedStakeholders];
      
      console.log('👥 All members loaded:', {
        employees: transformedEmployees.length,
        stakeholders: transformedStakeholders.length,
        total: allMembers.length
      });
      
      setAllStakeholders(allMembers);
    } catch (e) { 
      console.error("❌ Failed to fetch employees:", e);
      console.error("Employee response data:", e.response?.data);
      console.error("Employee response status:", e.response?.status);
      console.error("Employee response headers:", e.response?.headers);
      // If even employees fail, set to empty array
      setAllStakeholders([]); 
    }
  };


  const handleAddStakeholder = async (memberId) => {
    console.log('🔄 Adding member:', { memberId, selectedProject: selectedProject?.id });
    setModalLoading(true);
    setModalError('');
    try {
      // Find the member in our allStakeholders list
      const member = allStakeholders.find(m => m.id === memberId);
      console.log('👤 Found member:', member);
      
      if (!member) {
        throw new Error('Member not found');
      }

      let requestData;
      if (member.type === 'employee') {
        // For employees, use the actual employee ID (not the prefixed one)
        console.log('🔍 Employee assignment details:', {
          memberEmployeeId: member.employee_id,
          memberEmployeeIdType: typeof member.employee_id,
          projectId: selectedProject.id,
          projectIdType: typeof selectedProject.id,
          memberName: member.display_name || member.full_name
        });
        
        requestData = {
          employee_id: member.employee_id,
          role_in_project: 'team_member',
          involvement_level: 'medium',
          impact_level: 'medium',
          engagement_status: 'identified'
        };
      } else {
        // For stakeholders, use stakeholder_id
        requestData = {
          stakeholder_id: member.stakeholder_id,
          role_in_project: 'stakeholder',
          involvement_level: 'medium',
          impact_level: 'medium',
          engagement_status: 'identified'
        };
      }

      console.log('📤 Sending request:', {
        url: `/projects/${selectedProject.id}/stakeholders`,
        data: requestData,
        memberType: member.type,
        memberEmployeeId: member.employee_id,
        memberStakeholderId: member.stakeholder_id,
        fullMemberData: member,
        selectedProjectId: selectedProject.id,
        requestDataStringified: JSON.stringify(requestData, null, 2)
      });

      const response = await apiClient.post(`/projects/${selectedProject.id}/stakeholders`, requestData);
      console.log('✅ Add successful:', response.data);
      
      await fetchProjectStakeholders(selectedProject.id);
      
      // If the charter form is open for this project, update the stakeholders there too
      if (editingProject && editingProject.id === selectedProject.id) {
        await fetchCurrentProjectStakeholders(selectedProject.id);
      }
      
      // Show success message
      setModalError(`✅ Successfully added ${member.display_name || member.full_name} to the project!`);
      setTimeout(() => setModalError(''), 3000);
      
    } catch (e) {
      console.error("❌ Failed to add member:", e);
      console.error("Error response:", e.response?.data);
      console.error("Error status:", e.response?.status);
      
      const member = allStakeholders.find(m => m.id === memberId);
      const errorMsg = e.response?.data?.error || e.message || 'Unknown error occurred';
      setModalError(`❌ Failed to add ${member?.display_name || member?.full_name || 'member'}: ${errorMsg}`);
    } finally {
      setModalLoading(false);
    }
  };
  
  const handleRemoveStakeholder = async (stakeholderId) => {
    const stakeholder = projectStakeholders.find(s => {
      // Handle different ID formats for finding the stakeholder
      const sId = s.id || s.stakeholder_id || s.employee_id;
      return sId === stakeholderId || s.id === stakeholderId;
    });
    
    const stakeholderName = stakeholder?.display_name || stakeholder?.full_name || `${stakeholder?.first_name} ${stakeholder?.last_name}` || 'this member';
    
    // Direct removal without confirmation modal
    setModalError('');
    try {
      // Determine the correct endpoint and ID based on the stakeholder type
      let deleteUrl;
      let deleteId;
      
      if (stakeholder?.source === 'employee_table' || stakeholder?.type === 'employee') {
        // For employees, use the legacy employee endpoint
        deleteId = stakeholder.employee_id || stakeholder.id;
        deleteUrl = `/projects/${selectedProject.id}/stakeholders/${deleteId}`;
      } else if (stakeholder?.source === 'stakeholder_table' || stakeholder?.type === 'stakeholder') {
        // For comprehensive stakeholders, use the v2 endpoint
        deleteId = stakeholder.stakeholder_id || stakeholder.id;
        deleteUrl = `/v2/projects/${selectedProject.id}/stakeholders/${deleteId}`;
      } else {
        // Fallback - try to determine based on available IDs
        if (stakeholder?.employee_id) {
          deleteId = stakeholder.employee_id;
          deleteUrl = `/projects/${selectedProject.id}/stakeholders/${deleteId}`;
        } else if (stakeholder?.stakeholder_id) {
          deleteId = stakeholder.stakeholder_id;
          deleteUrl = `/v2/projects/${selectedProject.id}/stakeholders/${deleteId}`;
        } else {
          // Last resort - use the ID directly with legacy endpoint
          deleteId = stakeholder?.id || stakeholderId;
          deleteUrl = `/projects/${selectedProject.id}/stakeholders/${deleteId}`;
        }
      }
      
      console.log('🗑️ Removing member:', {
        stakeholder,
        deleteUrl,
        deleteId,
        type: stakeholder?.type || 'unknown',
        source: stakeholder?.source || 'unknown'
      });
      
      await apiClient.delete(deleteUrl);
      
      // Show success message
      setModalError(`✅ Successfully removed ${stakeholderName} from the project!`);
      setTimeout(() => setModalError(''), 3000);
      
      await fetchProjectStakeholders(selectedProject.id);
      
      // If the charter form is open for this project, update the stakeholders there too
      if (editingProject && editingProject.id === selectedProject.id) {
        await fetchCurrentProjectStakeholders(selectedProject.id);
      }
    } catch (e) {
      console.error("Failed to remove member:", e);
      console.error("Error response:", e.response?.data);
      console.error("Error status:", e.response?.status);
      const errorMsg = e.response?.data?.error || e.message || 'Unknown error occurred';
      setModalError(`❌ Failed to remove ${stakeholderName}: ${errorMsg}`);
    }
  };
  // Stakeholder groups are project-specific, so no add/remove functionality needed
  // Groups are created directly for projects through the Stakeholders page

  const filteredStakeholders = allStakeholders.filter(stakeholder => {
    // Improved filtering logic - check if already assigned to project
    const isAlreadyAssigned = projectStakeholders.some(ps => {
      // For employees: compare employee_id with employee_id
      if (stakeholder.type === 'employee' && (ps.source === 'employee_table' || ps.type === 'employee')) {
        return ps.employee_id === stakeholder.employee_id || ps.id === stakeholder.employee_id;
      }
      // For stakeholders: compare stakeholder_id with stakeholder_id  
      if (stakeholder.type === 'stakeholder' && (ps.source === 'stakeholder_table' || ps.type === 'stakeholder')) {
        return ps.stakeholder_id === stakeholder.stakeholder_id || ps.id === stakeholder.stakeholder_id;
      }
      // Fallback comparison using IDs
      const psId = ps.id || ps.stakeholder_id || ps.employee_id;
      const stakeholderId = stakeholder.stakeholder_id || stakeholder.employee_id;
      return psId === stakeholderId;
    });
    
    // Create searchable text from all relevant fields
    const stakeholderName = `${stakeholder.first_name || ''} ${stakeholder.last_name || ''}`.trim();
    const searchableText = [
      stakeholderName,
      stakeholder.display_name || '',
      stakeholder.full_name || '',
      stakeholder.job_title || '',
      stakeholder.organization || '',
      stakeholder.email || ''
    ].join(' ').toLowerCase();
    
    // Check if matches search term (if any)
    const matchesSearch = !searchTerm || searchableText.includes(searchTerm.toLowerCase());
    
    const isAvailable = !isAlreadyAssigned && matchesSearch;
    
    // Debug logging for troubleshooting
    if (allStakeholders.length < 10) {
      console.log(`🔍 Filtering ${stakeholder.display_name || stakeholder.full_name}:`, {
        id: stakeholder.id,
        employee_id: stakeholder.employee_id,
        stakeholder_id: stakeholder.stakeholder_id,
        type: stakeholder.type,
        isAlreadyAssigned,
        matchesSearch,
        isAvailable
      });
    }
    
    return isAvailable;
  });
  // Filter project groups by search term
  const filteredGroups = projectGroups.filter(grp =>
    grp.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="projects-page">
      <div className="page-header">
        {/* <h2>Projects Overview</h2> Removed redundant title */}
        <button className="btn-primary add-project-btn" onClick={openCreateModal}>
          <FaPlus /> New Project
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {isLoading ? (
        <p>Loading projects...</p>
      ) : projects.length === 0 ? (
        <div className="empty-state">
          <FaProjectDiagram className="empty-state-icon" />
          <h3>No Projects Yet</h3>
          <p>Get started by creating your first project.</p>
          <button className="btn-primary" onClick={openCreateModal}>
            <FaPlus /> Create Your First Project
          </button>
        </div>
      ) : (
        <div className="projects-table-container">
          <table className="projects-table">
            <thead>
              <tr>
                <th>Project Name</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Phase</th>
                <th>Budget</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {projects.map(project => (
                <tr key={project.id}>
                  <td>{project.name}</td>
                  <td>
                    {project.project_priority ? (
                      <span className={`priority-badge priority-${project.project_priority.toLowerCase()}`}>
                        {project.project_priority}
                      </span>
                    ) : '-'}
                  </td>
                  <td>{project.status}</td>
                  <td>{project.project_phase}</td>
                  <td>
                    {project.estimated_budget ? 
                      `${project.currency || 'USD'} ${parseFloat(project.estimated_budget).toLocaleString()}` : '-'
                    }
                  </td>
                  <td>{project.start_date ? new Date(project.start_date).toLocaleDateString() : '-'}</td>
                  <td>{project.end_date ? new Date(project.end_date).toLocaleDateString() : '-'}</td>
                  <td>
                    <button onClick={() => openEditModal(project)} className="btn-icon" title="Edit"><FaEdit /></button>
                    <button onClick={() => {  openManageModal(project); }} className="btn-icon" title="Manage Stakeholders"><FaUsers /></button>
                    <button onClick={() => handleDeleteProject(project.id)} className="btn-icon" title="Delete"><FaTrash /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isModalOpen && (
        <div className="modal">
          <div className="modal-content">
            <button className="modal-close" onClick={closeModal}>&times;</button>
            
            {/* Conditional rendering: Wizard for Create, Tabs for Edit */}
            {!editingProject ? (
              // CREATE MODE: Multi-Step Wizard
              <>
                <h3>Create New Project</h3>
                {projectModalError && <div className="wizard-error">{projectModalError}</div>}
                
                {/* Progress Indicator */}
                <div className="wizard-progress">
                  <div className="wizard-steps">
                    {stepConfig.map((step) => (
                      <div 
                        key={step.id}
                        className={`wizard-step ${
                          step.id === currentStep ? 'current' : 
                          completedSteps.has(step.id) ? 'completed' : 
                          canProceedToStep(step.id) ? '' : 'disabled'
                        }`}
                        onClick={() => canProceedToStep(step.id) && goToStep(step.id)}
                      >
                        <div className="wizard-step-circle">
                          {completedSteps.has(step.id) ? '✓' : step.icon}
                        </div>
                        <div className="wizard-step-title">{step.title}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Step Content */}
                <div className="wizard-content">
                  {renderWizardStep()}
                </div>

                {/* Navigation */}
                <div className="wizard-navigation">
                  <div className="wizard-nav-buttons">
                    {currentStep > 1 && (
                      <button type="button" onClick={prevStep} className="wizard-btn secondary">
                        ← Previous
                      </button>
                    )}
                  </div>
                  
                  <div className="wizard-nav-buttons">
                    {currentStep < stepConfig.length ? (
                      <button 
                        type="button" 
                        onClick={nextStep} 
                        className="wizard-btn primary"
                        disabled={!validateStep(currentStep)}
                      >
                        Next →
                      </button>
                    ) : (
                      <button 
                        type="submit" 
                        onClick={handleSubmit}
                        className="wizard-btn success"
                        disabled={!validateStep(currentStep)}
                      >
                        ✅ Create Project
                      </button>
                    )}
                  </div>
                </div>
              </>
            ) : (
              // EDIT MODE: Tabbed Interface
              <>
                <h3>Edit Project: {editingProject.name}</h3>
                {projectModalError && <div className="modal-error">{projectModalError}</div>}
                {renderEditTabs()}
              </>
            )}
            
            <form onSubmit={handleSubmit} style={{display: 'none'}}>
              <div className="form-group">
                <label htmlFor="name">Project Name *</label>
                <input type="text" id="name" name="name" value={formData.name} onChange={handleInputChange} required />
              </div>
              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea id="description" name="description" value={formData.description} onChange={handleInputChange}></textarea>
              </div>
              <div className="form-group">
                <label htmlFor="start_date">Start Date</label>
                <input type="date" id="start_date" name="start_date" value={formData.start_date} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <label htmlFor="end_date">End Date</label>
                <input type="date" id="end_date" name="end_date" value={formData.end_date} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <label htmlFor="status">Status *</label>
                <select id="status" name="status" value={formData.status} onChange={handleInputChange} required>
                  {PROJECT_STATUSES.map(statusOption => (
                    <option key={statusOption} value={statusOption}>{statusOption}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="project_phase">Project Phase *</label>
                <select id="project_phase" name="project_phase" value={formData.project_phase} onChange={handleInputChange} required>
                  {PMI_PHASES.map(phase => (
                    <option key={phase} value={phase}>{phase}</option>
                  ))}
                </select>
              </div>

              {/* Project Charter Section */}
              <div className="form-section">
                <h4 className="section-title">📋 Project Charter Information</h4>
                
                <div className="form-group">
                  <label htmlFor="project_objective">Project Objective</label>
                  <textarea id="project_objective" name="project_objective" value={formData.project_objective} onChange={handleInputChange} placeholder="What is the main goal and purpose of this project?"></textarea>
                </div>
                
                <div className="form-group">
                  <label htmlFor="business_case">Business Case</label>
                  <textarea id="business_case" name="business_case" value={formData.business_case} onChange={handleInputChange} placeholder="Why is this project necessary? What business value will it deliver?"></textarea>
                </div>
                
                <div className="form-group">
                  <label htmlFor="scope_description">Scope Description</label>
                  <textarea id="scope_description" name="scope_description" value={formData.scope_description} onChange={handleInputChange} placeholder="What work will be included in this project?"></textarea>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="project_priority">Priority</label>
                    <select id="project_priority" name="project_priority" value={formData.project_priority} onChange={handleInputChange}>
                      <option value="">Select Priority</option>
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                      <option value="Critical">Critical</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="project_type">Project Type</label>
                    <input type="text" id="project_type" name="project_type" value={formData.project_type} onChange={handleInputChange} placeholder="e.g., Technology, Process Improvement, etc." />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="complexity_level">Complexity Level</label>
                    <select id="complexity_level" name="complexity_level" value={formData.complexity_level} onChange={handleInputChange}>
                      <option value="">Select Complexity</option>
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="change_impact_level">Change Impact Level</label>
                    <select id="change_impact_level" name="change_impact_level" value={formData.change_impact_level} onChange={handleInputChange}>
                      <option value="">Select Impact</option>
                      <option value="Low">Low</option>
                      <option value="Medium">Medium</option>
                      <option value="High">High</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Success Criteria & Metrics */}
              <div className="form-section">
                <h4 className="section-title">🎯 Success Criteria & Metrics</h4>
                
                <div className="form-group">
                  <label htmlFor="deliverables">Deliverables (one per line)</label>
                  <textarea id="deliverables" name="deliverables" value={formData.deliverables} onChange={handleInputChange} placeholder="New cloud-based application platform&#10;Migrated data and databases&#10;User training materials and sessions&#10;Technical documentation"></textarea>
                </div>
                
                <div className="form-group">
                  <label htmlFor="success_criteria">Success Criteria (one per line)</label>
                  <textarea id="success_criteria" name="success_criteria" value={formData.success_criteria} onChange={handleInputChange} placeholder="System uptime > 99.5%&#10;40% reduction in maintenance costs&#10;User adoption rate > 90%&#10;Zero data loss during migration"></textarea>
                </div>
                
                <div className="form-group">
                  <label htmlFor="key_metrics">Key Metrics (one per line)</label>
                  <textarea id="key_metrics" name="key_metrics" value={formData.key_metrics} onChange={handleInputChange} placeholder="System response time < 2 seconds&#10;User satisfaction score > 4.5/5&#10;ROI achieved within 18 months&#10;Support ticket reduction by 60%"></textarea>
                </div>
              </div>

              {/* Budget & Resources */}
              <div className="form-section">
                <h4 className="section-title">💰 Budget & Resources</h4>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="estimated_budget">Estimated Budget</label>
                    <input type="number" step="0.01" id="estimated_budget" name="estimated_budget" value={formData.estimated_budget} onChange={handleInputChange} placeholder="0.00" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="currency">Currency</label>
                    <select id="currency" name="currency" value={formData.currency} onChange={handleInputChange}>
                      <option value="USD">USD</option>
                      <option value="CAD">CAD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                    </select>
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="budget_breakdown">Budget Breakdown (category: amount)</label>
                  <textarea id="budget_breakdown" name="budget_breakdown" value={formData.budget_breakdown} onChange={handleInputChange} rows="3" placeholder="Software Licenses: 650000&#10;Implementation Services: 800000&#10;Training: 150000&#10;Infrastructure: 200000&#10;Contingency: 50000"></textarea>
                </div>
                
                <div className="form-group">
                  <label htmlFor="project_sponsor">Project Sponsor</label>
                  <input type="text" id="project_sponsor" name="project_sponsor" value={formData.project_sponsor} onChange={handleInputChange} placeholder="Name and title of project sponsor" />
                </div>
              </div>

              {/* Stakeholders & Timeline */}
              <div className="form-section">
                <h4 className="section-title">👥 Stakeholders & Timeline</h4>
                
                <div className="form-group">
                  <label>Key Stakeholders</label>
                  {currentProjectStakeholders.length === 0 ? (
                    <div className="stakeholder-placeholder">
                      <p>No stakeholders assigned to this project yet.</p>
                      <small>
                        💡 <strong>Tip:</strong> Use the <strong>👥 Users</strong> button in the project table to assign stakeholders to this project.
                        Once assigned, they will automatically appear here in the project charter.
                      </small>
                    </div>
                  ) : (
                    <div className="stakeholders-display">
                      <div className="stakeholders-list">
                        {currentProjectStakeholders.map((stakeholder, index) => (
                          <div key={stakeholder.id} className="stakeholder-item">
                            <FaUsers className="stakeholder-icon" />
                            <div className="stakeholder-info">
                              <span className="stakeholder-name">
                                {stakeholder.display_name || stakeholder.full_name || `${stakeholder.first_name} ${stakeholder.last_name}`}
                              </span>
                              <span className="stakeholder-role">
                                {stakeholder.job_title || stakeholder.organization || 'No title'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                      <small className="stakeholder-note">
                        💡 To modify stakeholders, use the <strong>👥 Users</strong> button in the project table.
                      </small>
                    </div>
                  )}
                </div>
                
                <div className="form-group">
                  <label htmlFor="affected_departments">Affected Departments (one per line)</label>
                  <textarea id="affected_departments" name="affected_departments" value={formData.affected_departments} onChange={handleInputChange} placeholder="Information Technology&#10;Operations&#10;Finance&#10;Customer Service"></textarea>
                </div>
                
                <div className="form-group">
                  <label htmlFor="key_milestones">Key Milestones (milestone: date)</label>
                  <textarea id="key_milestones" name="key_milestones" value={formData.key_milestones} onChange={handleInputChange} placeholder="Project Kickoff: Week 1&#10;Requirements Finalization: Week 4&#10;System Design Complete: Week 8&#10;Development Phase: Week 12&#10;Testing & UAT: Week 20&#10;Go-Live: Week 24"></textarea>
                </div>
                
                <div className="form-group">
                  <label htmlFor="critical_path_items">Critical Path Items (one per line)</label>
                  <textarea id="critical_path_items" name="critical_path_items" value={formData.critical_path_items} onChange={handleInputChange} placeholder="Data mapping and migration strategy&#10;Integration testing with existing systems&#10;User acceptance testing&#10;Production deployment"></textarea>
                </div>
              </div>

              {/* Risk Management */}
              <div className="form-section">
                <h4 className="section-title">⚠️ Risk Management</h4>
                
                <div className="form-group">
                  <div className="risk-header">
                    <label>Project Risks</label>
                    <button type="button" className="btn-secondary" onClick={addRisk}>
                      <FaPlus /> Add Risk
                    </button>
                  </div>
                  
                  {projectRisks.length === 0 ? (
                    <div className="empty-risks">
                      <p>No risks identified yet. Click "Add Risk" to get started.</p>
                    </div>
                  ) : (
                    <div className="risks-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Risk Description</th>
                            <th>Impact</th>
                            <th>Probability</th>
                            <th>Mitigation Strategy</th>
                            <th>Owner</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {projectRisks.map((risk) => (
                            <tr key={risk.id}>
                              <td>
                                <input
                                  type="text"
                                  value={risk.description}
                                  onChange={(e) => updateRisk(risk.id, 'description', e.target.value)}
                                  placeholder="Describe the risk"
                                  className="risk-input"
                                />
                              </td>
                              <td>
                                <select
                                  value={risk.impact}
                                  onChange={(e) => updateRisk(risk.id, 'impact', e.target.value)}
                                  className="risk-select"
                                >
                                  <option value="Low">Low</option>
                                  <option value="Medium">Medium</option>
                                  <option value="High">High</option>
                                  <option value="Critical">Critical</option>
                                </select>
                              </td>
                              <td>
                                <select
                                  value={risk.probability}
                                  onChange={(e) => updateRisk(risk.id, 'probability', e.target.value)}
                                  className="risk-select"
                                >
                                  <option value="Low">Low</option>
                                  <option value="Medium">Medium</option>
                                  <option value="High">High</option>
                                  <option value="Very High">Very High</option>
                                </select>
                              </td>
                              <td>
                                <input
                                  type="text"
                                  value={risk.mitigation}
                                  onChange={(e) => updateRisk(risk.id, 'mitigation', e.target.value)}
                                  placeholder="How to mitigate this risk"
                                  className="risk-input"
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  value={risk.owner}
                                  onChange={(e) => updateRisk(risk.id, 'owner', e.target.value)}
                                  placeholder="Risk owner"
                                  className="risk-input"
                                />
                              </td>
                              <td>
                                <select
                                  value={risk.status}
                                  onChange={(e) => updateRisk(risk.id, 'status', e.target.value)}
                                  className="risk-select"
                                >
                                  <option value="Open">Open</option>
                                  <option value="In Progress">In Progress</option>
                                  <option value="Mitigated">Mitigated</option>
                                  <option value="Closed">Closed</option>
                                </select>
                              </td>
                              <td>
                                <button
                                  type="button"
                                  onClick={() => deleteRisk(risk.id)}
                                  className="btn-icon delete-risk"
                                  title="Delete Risk"
                                >
                                  <FaTrash />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                
                <div className="form-group">
                  <label htmlFor="constraints">Constraints (one per line)</label>
                  <textarea id="constraints" name="constraints" value={formData.constraints} onChange={handleInputChange} placeholder="Must maintain 24/7 operations&#10;Budget cap of $2M&#10;Must complete before fiscal year end&#10;Limited testing environment"></textarea>
                </div>
                
                <div className="form-group">
                  <label htmlFor="assumptions">Assumptions (one per line)</label>
                  <textarea id="assumptions" name="assumptions" value={formData.assumptions} onChange={handleInputChange} placeholder="All stakeholders will participate in training&#10;Current infrastructure can support new system&#10;Third-party integrations will be stable&#10;Team members will remain available"></textarea>
                </div>
                
                <div className="form-group">
                  <label htmlFor="change_readiness_score">Change Readiness Score (1-10)</label>
                  <input type="number" min="1" max="10" id="change_readiness_score" name="change_readiness_score" value={formData.change_readiness_score} onChange={handleInputChange} placeholder="1-10" />
                </div>
              </div>

              {/* External Integration & Approval */}
              <div className="form-section">
                <h4 className="section-title">🔗 External Integration & Approval</h4>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="external_project_id">External Project ID</label>
                    <input type="text" id="external_project_id" name="external_project_id" value={formData.external_project_id} onChange={handleInputChange} placeholder="ERP or external system ID" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="external_system_url">External System URL</label>
                    <input type="url" id="external_system_url" name="external_system_url" value={formData.external_system_url} onChange={handleInputChange} placeholder="https://..." />
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="approval_status">Approval Status</label>
                    <select id="approval_status" name="approval_status" value={formData.approval_status} onChange={handleInputChange}>
                      <option value="draft">Draft</option>
                      <option value="in_review">In Review</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="approved_by">Approved By</label>
                    <input type="text" id="approved_by" name="approved_by" value={formData.approved_by} onChange={handleInputChange} placeholder="Name and title of approver" />
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="approved_date">Approval Date</label>
                  <input type="date" id="approved_date" name="approved_date" value={formData.approved_date} onChange={handleInputChange} />
                </div>
              </div>

              {/* Custom Fields */}
              <div className="form-section">
                <h4 className="section-title">🏷️ Custom Fields & Tags</h4>
                
                <div className="form-group">
                  <label htmlFor="custom_fields">Custom Fields (field: value)</label>
                  <textarea id="custom_fields" name="custom_fields" value={formData.custom_fields} onChange={handleInputChange} placeholder="compliance_level: SOX Required&#10;vendor: TechCorp Solutions&#10;methodology: Agile"></textarea>
                </div>
                
                <div className="form-group">
                  <label htmlFor="tags">Tags (one per line)</label>
                  <textarea id="tags" name="tags" value={formData.tags} onChange={handleInputChange} placeholder="modernization&#10;high-priority&#10;technology&#10;cloud"></textarea>
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn-primary">{editingProject ? 'Save Changes' : 'Create Project'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showManageModal && selectedProject && (
        <div className="modal manage-modal">
          <div className="modal-content">
            <button className="modal-close" onClick={closeManageModal}><FaTimes /></button>
            <h2 style={{marginBottom: 8}}>Manage Stakeholders & Groups</h2>
            <h3 style={{marginBottom: 16, color: '#1a365d'}}>{selectedProject.name}</h3>
            <div className="toggle-btn-group" style={{marginBottom: 20, display: 'flex', gap: 8}}>
              <button
                onClick={() => setManageType('stakeholders')}
                className={`toggle-btn${manageType === 'stakeholders' ? ' active' : ''}`}
                style={{
                  background: manageType === 'stakeholders' ? '#1a365d' : '#eaf1fa',
                  color: manageType === 'stakeholders' ? '#fff' : '#1a365d',
                  border: `2px solid #1a365d`,
                  borderRadius: 6,
                  fontWeight: 600,
                  padding: '8px 20px',
                  cursor: 'pointer',
                  transition: 'background 0.2s, color 0.2s',
                  boxShadow: manageType === 'stakeholders' ? '0 2px 8px rgba(26,54,93,0.08)' : 'none',
                }}
                aria-pressed={manageType === 'stakeholders'}
              >
                Stakeholders
              </button>
              <button
                onClick={() => setManageType('groups')}
                className={`toggle-btn${manageType === 'groups' ? ' active' : ''}`}
                style={{
                  background: manageType === 'groups' ? '#1a365d' : '#eaf1fa',
                  color: manageType === 'groups' ? '#fff' : '#1a365d',
                  border: `2px solid #1a365d`,
                  borderRadius: 6,
                  fontWeight: 600,
                  padding: '8px 20px',
                  cursor: 'pointer',
                  transition: 'background 0.2s, color 0.2s',
                  boxShadow: manageType === 'groups' ? '0 2px 8px rgba(26,54,93,0.08)' : 'none',
                }}
                aria-pressed={manageType === 'groups'}
              >
                Groups
              </button>
            </div>
            <input
              type="text"
              className="search-input"
              placeholder={`Search ${manageType}...`}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{ marginBottom: 16, width: '100%' }}
            />
            {modalError && <div className="modal-error">{modalError}</div>}
            {modalLoading && <div className="modal-loading">Loading...</div>}
            {manageType === 'stakeholders' ? (
              <>
                <div className="modal-section assigned-section" style={{background:'#eaf1fa', borderRadius:8, padding:12, marginBottom:16}}>
                  <h4 style={{color:'#1a365d', marginBottom:8}}>Assigned Stakeholders</h4>
                  <ul className="assigned-list">
                    {projectStakeholders.length === 0 ? (
                      <li className="empty-state">No stakeholders assigned.</li>
                    ) : (
                      projectStakeholders.map(stakeholder => (
                        <li key={stakeholder.id} className="assigned-item" style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'6px 0'}}>
                          <span className="assigned-name" style={{fontWeight:600, display:'inline-flex', alignItems:'center'}}>
                            <FaUsers style={{color:'#1a365d',marginRight:6}}/>
                            {stakeholder.display_name || `${stakeholder.first_name} ${stakeholder.last_name}`} 
                            <span style={{fontWeight:400, color:'#555'}}>
                              ({stakeholder.job_title || stakeholder.organization || 'No title'})
                            </span>
                          </span>
                          <button className="remove-btn" style={{color:'#ff6b00',background:'none',border:'none',cursor:'pointer'}} onClick={() => handleRemoveStakeholder(stakeholder.id)} title="Remove"><FaMinus /></button>
                        </li>
                      ))
                    )}
                  </ul>
                </div>
                <hr className="section-divider" style={{border:'none',borderTop:'1px solid #e0e0e0',margin:'16px 0'}}/>
                <div className="modal-section available-section" style={{background:'#f7f7f7', borderRadius:8, padding:12}}>
                  <h4 style={{color:'#888', marginBottom:8}}>Available Members (Employees & Stakeholders)</h4>
                  <ul className="available-list">
                    {filteredStakeholders.length === 0 ? (
                      <li className="empty-state">No members to add.</li>
                    ) : (
                      filteredStakeholders.map(member => (
                        <li key={member.id} className="available-item" style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'6px 0'}}>
                          <span className="available-name" style={{fontWeight:600, display:'inline-flex', alignItems:'center'}}>
                            <FaUsers style={{color:'#bbb',marginRight:6}}/>
                            {member.display_name || member.full_name} 
                            <span style={{color:'#888'}}>
                              ({member.job_title || member.organization || 'No title'})
                            </span>
                            <span style={{
                              backgroundColor: member.type === 'employee' ? '#e3f2fd' : '#f3e5f5',
                              color: member.type === 'employee' ? '#1976d2' : '#7b1fa2',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontSize: '0.75em',
                              marginLeft: '8px',
                              fontWeight: 'bold'
                            }}>
                              {member.type === 'employee' ? 'Employee' : 'Stakeholder'}
                            </span>
                          </span>
                          <button className="add-btn" style={{color:'#1a365d',background:'none',border:'none',cursor:'pointer'}} onClick={() => handleAddStakeholder(member.id)} title="Add"><FaPlus /></button>
                        </li>
                      ))
                    )}
                  </ul>
                </div>
              </>
            ) : (
              <>
                <div className="modal-section" style={{background:'#eaf1fa', borderRadius:8, padding:12}}>
                  <h4 style={{color:'#1a365d', marginBottom:8}}>Stakeholder Groups for this Project</h4>
                  <ul className="group-list">
                    {filteredGroups.length === 0 ? (
                      <li className="empty-state">
                        No stakeholder groups found for this project.
                        <br />
                        <small style={{color:'#666', marginTop:4, display:'block'}}>
                          Create stakeholder groups in the Stakeholders page.
                        </small>
                      </li>
                    ) : (
                      filteredGroups.map(grp => (
                        <li key={grp.id} className="group-item" style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'8px 0'}}>
                          <div style={{display:'flex', flexDirection:'column'}}>
                            <span className="group-name" style={{fontWeight:600, display:'inline-flex', alignItems:'center'}}>
                              <FaUsers style={{color:'#1a365d',marginRight:8}}/>
                              {grp.name}
                            </span>
                            {grp.description && (
                              <small style={{color:'#666', marginLeft:24}}>{grp.description}</small>
                            )}
                            <small style={{color:'#888', marginLeft:24}}>
                              {grp.member_count || 0} members
                            </small>
                          </div>
                        </li>
                      ))
                    )}
                  </ul>
                </div>
                <div style={{marginTop:16, padding:12, background:'#f8f9fa', borderRadius:8, textAlign:'center'}}>
                  <small style={{color:'#666'}}>
                    💡 <strong>Tip:</strong> Stakeholder groups are created and managed in the Stakeholders page. 
                    Each group belongs to exactly one project.
                  </small>
                </div>
              </>
            )}
            <div style={{marginTop: 24, textAlign: 'right'}}>
              <button className="btn-secondary" onClick={closeManageModal}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={closeConfirmationModal}
        onConfirm={confirmationModal.onConfirm}
        title={confirmationModal.title}
        message={confirmationModal.message}
        confirmButtonText={confirmationModal.confirmText}
        cancelButtonText={confirmationModal.cancelText}
        type={confirmationModal.type}
        isLoading={confirmationModal.isLoading}
      />
    </div>
  );
}

export default Projects;



